import { useState } from "react";
import { toast } from "react-toastify";
import { When } from "react-if";
import { useCopyToClipboard } from "usehooks-ts";
import { ClipboardIcon, KeyIcon } from "@heroicons/react/24/outline";
import { ApiTokenScope } from "shared";

import { Modal } from "../../../../../common/Atoms/Modal";
import { Button } from "../../../../../common/Atoms/Button";
import { CheckBox } from "../../../../../common/Atoms/CheckBox";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Notification } from "../../../../../common/Atoms/Notification";
import { useCreateApiTokenMutation } from "../../../../services/api/apiTokenApi/apiToken";

interface ApiTokenModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  organisationId: string;
}

export function ApiTokenModal({ open, setOpen, organisationId }: ApiTokenModalProps) {
  const [createApiToken, { isLoading }] = useCreateApiTokenMutation();
  const [plaintextToken, setPlaintextToken] = useState<string | null>(null);
  const [writeAccess, setWriteAccess] = useState(false);
  const [, copy] = useCopyToClipboard();

  function handleClose() {
    setOpen(false);
    // Clear the revealed token once the modal is dismissed so it is never shown again.
    setPlaintextToken(null);
    setWriteAccess(false);
  }

  async function handleCreate() {
    const scopes = writeAccess ? [ApiTokenScope.READ, ApiTokenScope.WRITE] : [ApiTokenScope.READ];

    try {
      const response = await createApiToken({ organisationId, scopes }).unwrap();
      setPlaintextToken(response.plaintextToken);
    }
    catch {
      toast.error(`Could not create API token`);
    }
  }

  async function handleCopy() {
    if (!plaintextToken) {
      return;
    }

    try {
      await copy(plaintextToken);
      toast.success(`Copied to clipboard`, { autoClose: 200 });
    }
    catch {
      toast.error(`Failed to copy to clipboard`);
    }
  }

  return (
    <Modal
      open={ open }
      setOpen={ handleClose }
      title={ `Create API Token` }
      icon={ <KeyIcon className={ `text-paidniceGreen-500 h-5 w-5 mr-2` } /> }
    >
      <When condition={ !plaintextToken }>
        <div className={ `space-y-4` }>
          <Paragraph>
            { `This will generate a new API token for accessing the Paidnice public API. The token will be shown only once.` }
          </Paragraph>

          <CheckBox
            name={ `writeAccess` }
            label={ `Allow write access` }
            description={ `Every token can read data. Write access also lets it update records, such as contact persons.` }
            checked={ writeAccess }
            onChange={ ({ value }) => setWriteAccess(value) }
          />

          <div className={ `flex justify-center space-x-4 mt-4` }>
            <Button
              color={ `paidniceGreen` }
              onClick={ handleCreate }
              loading={ isLoading }
            >
              { `Create token` }
            </Button>
            <Button
              color={ `gray` }
              onClick={ handleClose }
              disabled={ isLoading }
            >
              { `Cancel` }
            </Button>
          </div>
        </div>
      </When>

      <When condition={ !!plaintextToken }>
        <div className={ `space-y-4` }>
          <Notification
            type={ `warning` }
            title={ `Copy your token now` }
          >
            { `This is the only time the token will be shown. Store it somewhere safe - you will not be able to view it again.` }
          </Notification>

          <div className={ `flex items-center justify-between bg-gray-100 rounded-md p-3 break-all` }>
            <code className={ `text-xs text-gray-800 mr-2` }>
              { plaintextToken }
            </code>
            <ClipboardIcon
              className={ `h-5 w-5 shrink-0 text-paidniceGreen-500 cursor-pointer` }
              onClick={ handleCopy }
            />
          </div>

          <div className={ `flex justify-end mt-4` }>
            <Button
              color={ `paidniceGreen` }
              onClick={ handleClose }
            >
              { `Done` }
            </Button>
          </div>
        </div>
      </When>
    </Modal>
  );
}
